export const SERVICES_DATA = [
  {
    slug: "security-camera-installation",
    name: "CCTV & Security Cameras Installation",
    schemaName: "Security Camera Installation",
    h1: "Security Camera Installation",
    metaTitle: "Security Camera Installation | CCTV Installers | SafeHome Security Solutions",
    metaDescription:
      "Professional security camera installation for homes and businesses. HD & 4K CCTV systems with remote viewing, night vision and smart motion alerts. Free quotes.",
    heroText:
      "High-definition camera systems designed around your property — professionally wired, cleanly mounted, and set up so you can check in from anywhere.",
    image: "/assets/service-cctv.webp",
    imageAlt: "PTZ dome security camera installed on an office ceiling by SafeHome Security Solutions",
    overviewTitle: "Camera systems designed for your property",
    overview: [
      "A camera system only works if it is planned properly. We start with a walkthrough of your property to identify entrances, blind spots, lighting conditions and the best cable routes — then design a system that covers what matters without wasting cameras on what doesn't.",
      "We install HD and 4K bullet, dome and PTZ cameras with infrared night vision, paired with NVR recording and remote viewing on your phone. Every cable is run neatly, and every camera is aimed, focused and tested before we leave.",
    ],
    applications: {
      residential: ["Front doors, driveways and backyards", "Detached garages, sheds and cottages", "Indoor cameras for entryways and common areas"],
      commercial: ["Retail stores and showrooms", "Offices, warehouses and parking lots", "Apartment and multi-unit buildings"],
    },
    process: [
      { title: "Walkthrough & Design", desc: "Free on-site visit. We map camera positions, coverage angles and cable routes, then give you a written quote." },
      { title: "Professional Installation", desc: "Cameras are mounted securely and wired where possible for maximum reliability, with concealed, tidy cabling." },
      { title: "Setup & Configuration", desc: "Recording schedules, motion zones and smart alerts are configured, and remote viewing is set up on your devices." },
      { title: "Training & Support", desc: "We walk you through the app and playback, and your installation is backed by our 3-year warranty." },
    ],
    benefits: [
      "Deter theft, vandalism and unwanted visitors",
      "Clear recorded evidence when an incident happens",
      "Check in live from your phone, anywhere",
      "Night vision coverage around the clock",
      "One system that can grow with your property",
    ],
    faqs: [
      { q: "Wired or wireless cameras — which is better?", a: "Wired cameras are more reliable and never need batteries charged, so we recommend them whenever cables can be run. Wireless options work well for spots where wiring isn't practical — we'll recommend the right mix during your walkthrough." },
      { q: "How many cameras do I need?", a: "Most homes need 4–8 cameras to cover entrances, the driveway and the backyard. Businesses vary more. After a free walkthrough we give you an exact recommendation — never more than you need." },
      { q: "Can I watch my cameras on my phone?", a: "Yes. Every system we install includes remote viewing on iPhone, Android and desktop, with push alerts when motion is detected." },
      { q: "How long is footage stored?", a: "That depends on the recorder size and resolution — typically 2–4 weeks of continuous recording. We'll size the storage to your needs when designing the system." },
    ],
    related: ["access-control", "video-doorbell-intercom", "smart-locks"],
  },
  {
    slug: "access-control",
    name: "Access Control Systems",
    schemaName: "Access Control System Installation",
    h1: "Access Control Systems",
    metaTitle: "Access Control Systems Installation | SafeHome Security Solutions",
    metaDescription:
      "Access control installation for offices, multi-unit buildings and homes. Keycard, fob and keypad entry systems with user management and audit trails. Free quotes.",
    heroText:
      "Keycard, fob and keypad entry systems that let you control exactly who gets in — and when — without ever cutting another key.",
    image: "/assets/service-access.webp",
    imageAlt: "Keycard tapping a door access control keypad reader installed by SafeHome Security Solutions",
    overviewTitle: "Keyless entry, managed properly",
    overview: [
      "Access control replaces keys with credentials you can manage. Issue a fob or code in seconds, revoke it just as fast, and see exactly who entered and when — no more wondering how many copies of a key exist.",
      "We install keypad readers, keycard and fob systems, electric strikes and magnetic locks, all wired cleanly and programmed to your schedule. Systems can stand alone on one door or scale across an entire building.",
    ],
    applications: {
      residential: ["Short-term rentals and Airbnb units", "Home offices and private studios", "Gated entries and garages"],
      commercial: ["Office entries and staff-only areas", "Apartment and multi-unit buildings", "Server rooms, stock rooms and restricted areas"],
    },
    process: [
      { title: "Door & Hardware Assessment", desc: "We inspect each door, frame and lock type and recommend the right reader and locking hardware." },
      { title: "Clean Installation", desc: "Readers, strikes and controllers are installed and wired neatly, with everything labelled." },
      { title: "Programming", desc: "Users, schedules and access levels are configured — who can go where, and when." },
      { title: "Handover & Support", desc: "We train you to add and remove users yourself, and we're a phone call away when you need help." },
    ],
    benefits: [
      "No more rekeying when staff or tenants change",
      "Grant or revoke access in seconds",
      "Audit trail of every entry",
      "Schedules that lock and unlock automatically",
      "Integrates with cameras and intercoms",
    ],
    faqs: [
      { q: "What happens if the power goes out?", a: "We configure doors to fail safe or fail secure depending on the door's purpose, and controllers can be backed up with battery power so your building stays secure and functional." },
      { q: "Can I manage users myself?", a: "Yes. Modern systems have simple apps or web dashboards where you can add a fob, create a temporary code or remove a user in under a minute." },
      { q: "Cards, fobs, codes or phones — which should we use?", a: "It depends on your doors and users. Many sites mix them: fobs for staff, temporary codes for cleaners or contractors. We'll recommend the simplest option during the assessment." },
      { q: "Can access control work with my cameras?", a: "Yes — we can link door events to camera bookmarks so you can instantly see video of every entry. It's one of the biggest advantages of having one team handle both." },
    ],
    related: ["security-camera-installation", "video-doorbell-intercom", "smart-locks"],
  },
  {
    slug: "video-doorbell-intercom",
    name: "Intercom & Video Doorbell Installation",
    schemaName: "Video Doorbell and Intercom Installation",
    h1: "Video Doorbell & Intercom Installation",
    metaTitle: "Video Doorbell & Intercom Installation | SafeHome Security Solutions",
    metaDescription:
      "Video doorbell and intercom installation for homes, offices and multi-unit buildings. See and speak to visitors from anywhere. Free quotes.",
    heroText:
      "See, hear and speak to whoever is at the door — from the couch, the office, or the other side of the world.",
    image: "/assets/service-doorbell.webp",
    imageAlt: "Video intercom and doorbell system installed at a building entrance by SafeHome Security Solutions",
    overviewTitle: "Never miss a visitor or delivery again",
    overview: [
      "A video doorbell is the most-used security device you'll own. Motion alerts show you visitors and deliveries the moment they arrive, and two-way talk lets you answer the door from anywhere.",
      "For multi-unit buildings and businesses, we install full intercom systems — video entry panels, in-suite answering, and door release — wired properly so they work every time, not just when the WiFi feels like it.",
    ],
    applications: {
      residential: ["Front doors and side entrances", "Driveway gates", "Cottages and seasonal properties"],
      commercial: ["Reception and delivery entrances", "Multi-tenant and apartment buildings", "Gated or after-hours entries"],
    },
    process: [
      { title: "Wiring & WiFi Check", desc: "We check your existing doorbell wiring, transformer and WiFi signal at the door so the unit works reliably from day one." },
      { title: "Mounting & Connection", desc: "The doorbell or intercom panel is mounted securely, level, and connected to power and network." },
      { title: "App & Alert Setup", desc: "We connect the app on your phones, set motion zones and alerts, and add family members or staff." },
      { title: "Testing & Demo", desc: "We test live calls, motion alerts and door release, then show you how everything works." },
    ],
    benefits: [
      "Answer your door from anywhere",
      "Package and visitor alerts in real time",
      "Two-way talk with clear audio",
      "Recorded clips of every visit",
      "Deters doorstep theft before it happens",
    ],
    faqs: [
      { q: "Do I need existing doorbell wiring?", a: "Not always. Wired power is best, but battery-powered models work well where no wiring exists. We check both options during your quote." },
      { q: "My door is far from the router — will it work?", a: "Weak WiFi at the door is the most common doorbell problem. We test the signal first and can add an access point if needed so your doorbell never drops offline." },
      { q: "Do video doorbells need a subscription?", a: "Many features work without one, but cloud recording usually requires a small plan from the manufacturer. We'll explain the exact costs of the model you choose before installing — no surprises." },
      { q: "Can you install intercoms for apartment buildings?", a: "Yes. We install multi-unit video intercoms with in-suite answering and door release, wired or IP-based depending on the building." },
    ],
    related: ["smart-locks", "security-camera-installation", "wifi-access-points"],
  },
  {
    slug: "structured-cabling",
    name: "Structured Cabling",
    schemaName: "Structured Cabling Installation",
    h1: "Structured Cabling Installation",
    metaTitle: "Structured Cabling & Network Cabling Services | SafeHome Security Solutions",
    metaDescription:
      "Structured cabling installation: Cat6 network cabling, patch panels, server racks and clean cable management for homes and businesses. Free quotes.",
    heroText:
      "Clean, labelled, certified cabling — the reliable backbone behind your network, cameras, WiFi and AV.",
    image: "/assets/gallery-rack-blue.webp",
    imageAlt: "Neatly dressed blue Cat6 network cabling on a patch panel installed by SafeHome Security Solutions",
    overviewTitle: "The wiring your whole building depends on",
    overview: [
      "Every camera, access point, TV and workstation is only as good as the cable behind it. We install Cat6 and Cat6a runs, patch panels and racks that are dressed neatly, labelled clearly and tested properly — so adding or fixing anything later takes minutes, not days.",
      "Whether it's a new build, a renovation, or untangling a comms room that's grown wild over the years, we leave you with cabling that's organized, documented and ready to grow.",
    ],
    applications: {
      residential: ["New builds and renovations", "Home offices and media rooms", "Pre-wiring for cameras, WiFi and AV"],
      commercial: ["Office network and phone drops", "Retail and restaurant fit-outs", "Server racks, comms rooms and cleanups"],
    },
    process: [
      { title: "Site Survey", desc: "We map drop locations, cable pathways and the rack or panel location, then quote the job in writing." },
      { title: "Cabling Plan", desc: "Every run is planned and labelled before the first cable is pulled — no guesswork, no crossed lines." },
      { title: "Installation", desc: "Cables are pulled, dressed and terminated neatly into patch panels, keystones and racks." },
      { title: "Test & Label", desc: "Every drop is tested, verified and labelled at both ends, with a layout you can keep on file." },
    ],
    benefits: [
      "Reliable wired performance WiFi can't match",
      "A tidy rack that's easy to maintain",
      "Every cable labelled and tested",
      "Ready for cameras, WiFi, AV and phones",
      "Adds value to new builds and renovations",
    ],
    faqs: [
      { q: "Cat6 or Cat6a — what do I need?", a: "Cat6 handles gigabit and most 10-gigabit runs in homes and small offices. Cat6a is better for longer runs or future-proofing commercial spaces. We'll recommend based on your distances and plans." },
      { q: "Can you clean up our existing mess of cables?", a: "Yes — rack and comms-room cleanups are some of our favourite jobs. We re-dress, re-label and document everything so the next person who opens that door smiles instead of sighs." },
      { q: "Do you test and certify the cabling?", a: "Every run is tested for continuity and performance and labelled at both ends. You get a clear map of what goes where." },
      { q: "How long does a cabling job take?", a: "A few drops in a home is usually a half day. Larger office jobs are scheduled in stages so your business keeps running while we work." },
    ],
    related: ["wifi-access-points", "security-camera-installation", "audio-visual-home-theatre"],
  },
  {
    slug: "wifi-access-points",
    name: "WiFi Access Points",
    schemaName: "WiFi Access Point Installation",
    h1: "WiFi Access Point Installation",
    metaTitle: "WiFi Access Point Installation | SafeHome Security Solutions",
    metaDescription:
      "WiFi access point installation for homes and businesses. Eliminate dead zones with professionally placed, wired access points and seamless coverage. Free quotes.",
    heroText:
      "Fast, seamless WiFi in every room — no more dead zones, buffering or dropped video calls.",
    image: "/assets/service-wifi.webp",
    imageAlt: "Ceiling-mounted WiFi access point installed in an office hallway by SafeHome Security Solutions",
    overviewTitle: "Coverage that actually reaches every corner",
    overview: [
      "A single router can't cover a whole house, let alone a business. Professionally placed access points — wired back to your network — give you one seamless network that follows you from the basement to the backyard without dropping.",
      "We install ceiling and wall-mounted access points, configure channels and roaming properly, and can set up separate guest networks so visitors never touch your main network or devices.",
    ],
    applications: {
      residential: ["Large and multi-storey homes", "Cottages, garages and outdoor spaces", "Home offices that can't afford dropouts"],
      commercial: ["Offices and boardrooms", "Restaurants, cafés and retail", "Warehouses and yards"],
    },
    process: [
      { title: "Coverage Assessment", desc: "We measure signal throughout your property to find dead zones and interference." },
      { title: "Placement Plan", desc: "Access point locations are planned for coverage and capacity — not just wherever there's a ceiling." },
      { title: "Wired Installation", desc: "APs are mounted and wired back to your network for full speed, then configured as one seamless network." },
      { title: "Speed Testing", desc: "We test coverage room by room and hand you a network that's ready for work, streaming and smart devices." },
    ],
    benefits: [
      "No more dead zones or buffering",
      "One network name, seamless roaming",
      "Secure guest network option",
      "Wired backhaul for full speed",
      "Stable enough for cameras and smart devices",
    ],
    faqs: [
      { q: "How many access points do I need?", a: "Most homes need 2–3; businesses depend on size and construction. Our coverage assessment gives you an exact number — and honest advice if you need fewer than you think." },
      { q: "Will this work with my existing internet provider?", a: "Yes. Access points work with any provider. We connect them to your existing modem or router, or can recommend an upgrade if yours is the bottleneck." },
      { q: "Can I give guests WiFi without sharing my password?", a: "Yes — we set up a separate guest network with its own password, isolated from your computers, cameras and smart devices." },
      { q: "Can you cover my backyard or detached garage?", a: "Yes. Outdoor-rated access points extend coverage to yards, patios and outbuildings — great for cameras and outdoor TVs too." },
    ],
    related: ["structured-cabling", "security-camera-installation", "smart-locks"],
  },
  {
    slug: "smart-locks",
    name: "Smart Light Switches & Smart Lock Installation",
    schemaName: "Smart Lock Installation",
    h1: "Smart Lock Installation",
    metaTitle: "Smart Lock Installation | Smart Home Security | SafeHome Security Solutions",
    metaDescription:
      "Smart lock installation for homes and rentals. Keyless entry with codes, fingerprints and phone control — plus smart light switches. Free quotes.",
    heroText:
      "Keyless entry with codes, fingerprints and your phone — plus smart light switches that make your home work around you.",
    image: "/assets/service-smartlock.webp",
    imageAlt: "Black smart lock with keypad and phone app control installed by SafeHome Security Solutions",
    overviewTitle: "Keys optional, control standard",
    overview: [
      "Smart locks let you unlock your door with a code, your fingerprint or your phone — and lock it from anywhere. Create temporary codes for guests, cleaners or contractors, get alerts when the door opens, and never hide a key under the mat again.",
      "We also install smart light switches, so your lights can follow schedules, respond to your voice, and make your home look lived-in when you're away. Everything is set up on your phone and demonstrated before we leave.",
    ],
    applications: {
      residential: ["Front and back doors", "Rental units and Airbnb properties", "Garage and side entrances"],
      commercial: ["Small offices and staff rooms", "Rental and property management", "Storage and utility rooms"],
    },
    process: [
      { title: "Door Check", desc: "We confirm your door and deadbolt are compatible and recommend a lock that fits — no drilling surprises." },
      { title: "Installation", desc: "The lock is installed, aligned and calibrated so it turns smoothly and locks reliably every time." },
      { title: "App Setup", desc: "We connect the lock to your phone, set up codes and fingerprints, and enable alerts and auto-lock." },
      { title: "Demo & Support", desc: "We show you how to manage codes and what to do if batteries run low — then back it with our 3-year warranty." },
    ],
    benefits: [
      "No more lost, copied or hidden keys",
      "Temporary codes for guests and contractors",
      "Lock and check status from anywhere",
      "Entry alerts tell you who's home",
      "Works with video doorbells and cameras",
    ],
    faqs: [
      { q: "Will a smart lock fit my door?", a: "Most standard doors with a deadbolt are compatible. We check your door first and recommend a model that fits properly — no forcing or ugly adapters." },
      { q: "What happens if the battery dies?", a: "You get low-battery warnings weeks in advance, and most models have a physical key or emergency power contacts as backup. You're never locked out." },
      { q: "How long do the batteries last?", a: "Typically 6–12 months depending on usage. We'll show you how to check levels in the app." },
      { q: "Can I still use a normal key?", a: "Most smart locks keep a key cylinder as backup, and some replace it entirely. We'll help you choose based on how you use your door." },
    ],
    related: ["video-doorbell-intercom", "access-control", "security-camera-installation"],
  },
  {
    slug: "audio-visual-home-theatre",
    name: "Home Theatre & Audio/Visual Systems",
    schemaName: "Home Theatre and Audio Visual Installation",
    h1: "Home Theatre & Audio/Visual Installation",
    metaTitle: "Home Theatre Installation | Audio Visual Services | SafeHome Security Solutions",
    metaDescription:
      "Home theatre installation and audio visual services: surround sound, projectors, whole-home audio and commercial AV. Clean wiring, expert tuning. Free quotes.",
    heroText:
      "Cinema-quality picture and sound at home — and clean, professional AV for boardrooms, restaurants and venues.",
    image: "/assets/service-theatre.webp",
    imageAlt: "Home theatre with large screen and surround speakers installed by SafeHome Security Solutions",
    overviewTitle: "Sound and picture, done right",
    overview: [
      "A great theatre room isn't about the most expensive gear — it's about the right gear, placed and tuned properly. We design and install home theatres, surround sound and whole-home audio with every cable hidden and every speaker positioned for the room, not just the box it came in.",
      "For businesses, we install boardroom AV, presentation systems, background music and displays that your staff can actually operate — one remote, no instruction manual required.",
    ],
    applications: {
      residential: ["Dedicated theatre rooms and media rooms", "Living room surround sound", "Whole-home and outdoor audio"],
      commercial: ["Boardrooms and meeting rooms", "Restaurants, gyms and retail audio", "Displays and digital signage"],
    },
    process: [
      { title: "Room Assessment", desc: "We look at the room's size, shape, lighting and acoustics before recommending anything." },
      { title: "System Design", desc: "Screen, speakers, amplification and control are designed to your room and budget — then quoted in writing." },
      { title: "Wiring & Installation", desc: "All cabling is concealed, speakers are mounted or placed precisely, and equipment is racked neatly." },
      { title: "Calibration & Demo", desc: "We tune audio levels and picture settings, then show you how to run everything with one simple control." },
    ],
    benefits: [
      "True cinema experience at home",
      "Every wire hidden, every speaker placed right",
      "Simple one-remote control",
      "Professionally tuned sound and picture",
      "Commercial AV your staff can actually use",
    ],
    faqs: [
      { q: "Can you hide all the wires?", a: "Yes — concealed wiring is standard on our installs. Cables are fished through walls and ceilings wherever possible, or neatly racewayed where they're not." },
      { q: "Do you supply the equipment, or install mine?", a: "Both. We can supply and install a complete system, or professionally install equipment you've already purchased." },
      { q: "Can you work with my existing speakers or receiver?", a: "Absolutely. We often upgrade rooms in stages — reusing good gear and replacing what's holding the system back." },
      { q: "Do you install outdoor audio and TVs?", a: "Yes — weather-rated speakers and displays for patios, decks and pool areas, wired back to your system." },
    ],
    related: ["tv-mounting", "structured-cabling", "wifi-access-points"],
  },
  {
    slug: "tv-mounting",
    name: "TV Mounting",
    schemaName: "TV Mounting Service",
    h1: "TV Mounting Service",
    metaTitle: "TV Mounting Service | Professional TV Wall Mounting | SafeHome Security Solutions",
    metaDescription:
      "Professional TV mounting service on drywall, brick and concrete. Secure, level mounting with concealed cables. Homes and businesses. Free quotes.",
    heroText:
      "Secure, perfectly level TV mounting on any wall — with cables concealed and the mess cleaned up before we leave.",
    image: "https://images.unsplash.com/photo-1586024486164-ce9b3d87e09f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx3YWxsJTIwbW91bnRlZCUyMHR2fGVufDB8fHx8MTc4NjY2MDQzM3ww&ixlib=rb-4.1.0&q=85",
    imageAlt: "Large flat screen TV professionally wall-mounted in a living room",
    overviewTitle: "Mounted right the first time",
    overview: [
      "A wall-mounted TV looks great — until it's crooked, too high, or pulled out of the drywall. We mount TVs of every size with the right anchors for your wall type, at the right height for your room, level to the millimetre.",
      "We conceal cables in-wall where possible, mount soundbars and shelves to match, and handle fixed, tilt and full-motion mounts. Commercial installs — waiting rooms, restaurants, signage — get the same clean treatment.",
    ],
    applications: {
      residential: ["Living rooms and family rooms", "Bedrooms and basements", "Above fireplaces and on brick"],
      commercial: ["Waiting rooms and lobbies", "Restaurants, bars and gyms", "Menu boards and digital signage"],
    },
    process: [
      { title: "Wall & Height Check", desc: "We locate studs, confirm the wall type, and mark the ideal viewing height with you before drilling." },
      { title: "Mount Installation", desc: "The bracket is anchored with hardware rated for your wall — drywall, brick, concrete or block." },
      { title: "Hang & Level", desc: "The TV is hung, levelled and secured, then connected to your devices and soundbar." },
      { title: "Conceal & Clean", desc: "Cables are concealed in-wall or in a neat raceway, packaging is removed, and the room is left spotless." },
    ],
    benefits: [
      "Perfectly level, at the right viewing height",
      "Anchored correctly for your wall type",
      "No visible cables",
      "Soundbar and accessory mounting available",
      "Fast, clean, done in one visit",
    ],
    faqs: [
      { q: "Can you mount a TV on brick or concrete?", a: "Yes. We carry masonry anchors and bits for brick, concrete and block walls — including fireplace installs." },
      { q: "Can the wires be completely hidden?", a: "In most cases yes. We fish cables through the wall where code allows, or use a slim paintable raceway where it doesn't." },
      { q: "Do I need to buy a mount first?", a: "No — we can supply a quality fixed, tilt or full-motion mount sized for your TV, or install one you've already bought." },
      { q: "How high should my TV be mounted?", a: "Ideally the centre of the screen sits near eye level from your main seat. We'll mark the height with you before drilling so it's exactly where you want it." },
    ],
    related: ["audio-visual-home-theatre", "wifi-access-points", "structured-cabling"],
  },
  {
    slug: "alarm-ulc-monitoring",
    name: "Alarm & ULC Monitoring",
    schemaName: "Alarm System Installation and ULC Monitoring",
    h1: "Alarm & ULC Monitoring",
    metaTitle: "Alarm Systems & ULC Monitoring Installation | SafeHome Security Solutions",
    metaDescription:
      "Alarm system installation with ULC-certified 24/7 monitoring for homes and businesses. Intrusion detection, fast dispatch and smartphone control. Free quotes.",
    heroText:
      "Professionally installed alarm systems connected to a ULC-certified monitoring station — watching over your property every hour of every day.",
    image: "/assets/service-alarm.webp",
    imageAlt: "Alarm panel with green armed indicator installed on a wall by SafeHome Security Solutions",
    overviewTitle: "Alarms that call for help, not just make noise",
    overview: [
      "A siren alone only annoys the neighbours. A monitored alarm contacts a ULC-certified monitoring station the instant a door, window or motion sensor trips — and help is dispatched whether you're home, at work, or on vacation.",
      "We design the sensor layout for your property, install door and window contacts, motion and glass-break detectors, and a modern panel you control from your phone. Then we activate professional monitoring so your system is never just noise.",
    ],
    applications: {
      residential: ["Whole-home intrusion protection", "Cottages and seasonal properties", "Garages, sheds and outbuildings"],
      commercial: ["Offices and professional suites", "Retail stores and showrooms", "Warehouses and storage facilities"],
    },
    process: [
      { title: "Security Assessment", desc: "We walk the property to identify entry points and vulnerabilities, then design the sensor layout." },
      { title: "Clean Installation", desc: "Panel, keypads and sensors are installed and wired or paired neatly — no dangling cables." },
      { title: "Programming", desc: "Zones, entry delays, user codes and phone alerts are configured to how you live and work." },
      { title: "Monitoring Activation", desc: "Your system is connected to the ULC-certified monitoring station and tested end to end." },
    ],
    benefits: [
      "24/7 ULC-certified professional monitoring",
      "Help dispatched even when you can't respond",
      "Arm, disarm and check status from your phone",
      "Instant alerts for doors, motion and glass break",
      "Recognized by insurers for monitored protection",
    ],
    faqs: [
      { q: "What does ULC-certified monitoring mean?", a: "ULC (Underwriters' Laboratories of Canada) certifies monitoring stations that meet strict standards for staffing, redundancy and response. It's the level of monitoring insurers look for — the station answers every signal, every time." },
      { q: "Can I use the alarm without monitoring?", a: "Yes, self-monitoring with phone alerts is possible, but professional monitoring is what gets help dispatched when you're asleep, away, or unreachable. Most customers choose monitoring for exactly that reason." },
      { q: "What about false alarms?", a: "We design the layout and entry delays around how you actually use the space, and the monitoring station verifies signals before dispatch — which keeps false alarms rare." },
      { q: "Can you take over my existing alarm system?", a: "Often, yes. We can assess your existing panel and sensors, and in many cases reactivate or upgrade them instead of replacing everything." },
    ],
    related: ["security-camera-installation", "smart-locks", "access-control"],
  },
  {
    slug: "holiday-permanent-lighting",
    name: "Holiday & Permanent Lighting",
    schemaName: "Holiday and Permanent Outdoor Lighting Installation",
    h1: "Holiday & Permanent Lighting Installation",
    metaTitle: "Permanent Holiday Lighting Installation | SafeHome Security Solutions",
    metaDescription:
      "Permanent outdoor lighting and holiday light installation for homes and businesses. Roofline lighting you control from your phone — installed once, enjoyed year-round. Free quotes.",
    heroText:
      "Professional roofline and architectural lighting — dazzling for the holidays, elegant every other night of the year.",
    image: "/assets/lighting-rgb.webp",
    imageAlt: "Whole house exterior at night with colourful permanent lighting installed along the roofline",
    overviewTitle: "One installation, every occasion",
    overview: [
      "Permanent outdoor lighting has changed the game. Instead of climbing a ladder every November, a low-profile LED track is installed once under your roofline — then every occasion from Christmas to Canada Day to a quiet warm-white evening is a tap away in the app.",
      "Prefer a classic seasonal display? We also design and install traditional holiday light installation — rooflines, windows, entrances and landscaping — with clean, professional results and timers that run the show for you.",
    ],
    types: [
      {
        title: "Holiday Lighting",
        desc: "Make your home the one people slow down to look at. We design and install seasonal roofline, window and entrance lighting with clean lines and balanced colour — no tangled strings, no crooked clips, no ladders.",
        points: ["Roofline and eavestrough outlines", "Window, door and entrance framing", "Tree, shrub and landscaping accents", "Timers and smart plugs set up for you"],
      },
      {
        title: "Permanent Lighting",
        desc: "Permanent holiday lighting is installed once and enjoyed all year. The slim track tucks under your eaves — nearly invisible by day — and at night you control millions of colours and patterns from your phone.",
        points: ["Low-profile track hidden under eaves", "Millions of colours and patterns via app", "Warm-white architectural look year-round", "Energy-efficient, weather-rated LEDs"],
      },
    ],
    applications: {
      residential: ["Rooflines, eaves and gables", "Windows, front entrances and porches", "Trees, shrubs and landscape features"],
      commercial: ["Storefronts and restaurants", "Office and building exteriors", "Seasonal displays for customer-facing properties"],
    },
    process: [
      { title: "Design Visit", desc: "We measure your roofline and features, show you colour and style options, and quote the design in writing." },
      { title: "Custom Fit", desc: "Track and lighting are cut and planned to your home's exact lines — corners, peaks and gables included." },
      { title: "Clean Installation", desc: "The system is mounted discreetly under the eaves with hidden wiring and a tidy controller location." },
      { title: "Scenes & App Setup", desc: "We connect the app, build your favourite scenes and schedules, and show you how to change everything yourself." },
    ],
    benefits: [
      "No more ladders, clips or tangled strings",
      "One install covers every holiday and event",
      "Millions of colours, controlled from your phone",
      "Energy-efficient LED, built for Canadian winters",
      "Curb appeal that lasts all year",
    ],
    faqs: [
      { q: "What's the difference between permanent and seasonal lighting?", a: "Seasonal holiday lighting goes up for the season with a classic look. Permanent lighting is a track installed once under your eaves that stays up year-round and changes to any colour or pattern from your phone — holidays, parties, or subtle warm white." },
      { q: "Is permanent lighting visible during the day?", a: "Barely. The low-profile track is colour-matched and tucked under the eave, so most people never notice it until it lights up at night." },
      { q: "Can I change the colours and patterns myself?", a: "Yes — the app gives you full control: colours, patterns, brightness, schedules and saved scenes. We set up your favourites before we leave." },
      { q: "Do you install lighting for businesses?", a: "Yes. Storefronts, restaurants and building exteriors benefit from the same permanent systems — brand colours every night, holiday scenes in December, all automated." },
    ],
    showcase: [
      {
        url: "https://images.unsplash.com/photo-1544397838-37a35169ebf0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
        alt: "House exterior at night with warm holiday lighting and an illuminated front-yard tree",
      },
      {
        url: "https://images.unsplash.com/photo-1664289340914-e88538627c02?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
        alt: "Roofline and eaves of a home outlined with decorative lighting at night",
      },
      {
        url: "https://images.unsplash.com/photo-1754522616772-c829b747541c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
        alt: "Premium home exterior at night with architectural accent lighting on the facade",
      },
    ],
    related: ["wifi-access-points", "security-camera-installation", "structured-cabling"],
  },
];
